/**
 * Demo Sequence Panel — 7-Step Demo Orchestrator
 *
 * This component orchestrates the 15-minute Agentlets governance demo.
 * It renders as a slide-out panel on the right side of the canvas,
 * replacing the properties panel when demo mode is active.
 *
 * Steps:
 *   1. (2 min) Auto-insert governance nodes into playbook template
 *   2. (2 min) Trigger workflow — real-time WebSocket execution
 *   3. (3 min) DPG scanning — PII detection + tokenization
 *   4. (2 min) Token vault — envelope encryption details
 *   5. (3 min) Compliance certificate — SOC 2 + hash chain
 *   6. (2 min) Agent trust score — probation transition
 *   7. (1 min) Governance proof — "Every step was governed"
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DpgScanPanel } from './DpgScanPanel';
import { TokenVaultExplorer } from './TokenVaultExplorer';
import { ComplianceCertificateViewer } from './ComplianceCertificateViewer';
import { AgentTrustScorePanel } from './AgentTrustScorePanel';
import { GovernanceProofSummary } from './GovernanceProofSummary';

const DEMO_STEPS = [
  {
    id: 1,
    title: 'Auto-Insert Protection',
    subtitle: 'Drag phishing triage playbook → enforcement nodes auto-woven',
    duration: '2 min',
    icon: '🏗',
    color: '#0d9488',
    instruction: 'Load the "Phishing Email Triage" template from the playbook library. Watch governance nodes auto-insert into the DAG.',
  },
  {
    id: 2,
    title: 'Live Execution',
    subtitle: 'Trigger workflow → real-time node-by-node updates via WebSocket',
    duration: '2 min',
    icon: '⚡',
    color: '#0d9488',
    instruction: 'Click "Execute" to run the workflow. Watch nodes light up in real-time as each step processes.',
  },
  {
    id: 3,
    title: 'Data Protection Guard',
    subtitle: 'DPG scans connector response → detects SSN → tokenizes in real-time',
    duration: '3 min',
    icon: '🛡',
    color: '#dc2626',
    instruction: 'Observe the DPG scan panel below. A connector response containing PII (SSN, email) is detected and tokenized automatically.',
  },
  {
    id: 4,
    title: 'Token Vault',
    subtitle: 'AES-256-GCM encrypted value with KMS-wrapped DEK',
    duration: '2 min',
    icon: '🔐',
    color: '#0a766b',
    instruction: 'Explore the token vault. Click any token to see the full envelope encryption: ciphertext, IV, and KMS-wrapped DEK.',
  },
  {
    id: 5,
    title: 'Compliance Certificate',
    subtitle: 'Generate SOC 2 certificate with SHA-256 hash chain verification',
    duration: '3 min',
    icon: '📜',
    color: '#065f46',
    instruction: 'Generate a SOC 2 compliance certificate. Verify the hash chain is intact from genesis to final event.',
  },
  {
    id: 6,
    title: 'Agent Trust Score',
    subtitle: 'Compliance score → trigger violation → watch probation transition',
    duration: '2 min',
    icon: '📊',
    color: '#d97706',
    instruction: 'View the agent\'s compliance score. A policy violation drops the score and triggers a probation transition.',
  },
  {
    id: 7,
    title: 'Protection Proof',
    subtitle: '"Every step was governed. Can your current platform prove the same?"',
    duration: '1 min',
    icon: '🏛',
    color: '#44403c',
    instruction: 'The finale: all 6 governance layers summarized in one view. Every step was governed.',
  },
];

export function DemoSequencePanel({
  // Step 1 data
  enforcementReport,
  // Step 2 state
  isExecuting,
  activeRunId,
  // Step 3 data (from WebSocket enforcement channel)
  enforcementEvents = [],
  // Step 4 data
  tokens = [],
  // Step 5 data
  certificate,
  onGenerateCertificate,
  // Step 6 data (from WebSocket compliance channel)
  complianceEvents = [],
  agentData,
  // Step 7 aggregation
  workflowName,
  executionDurationMs,
  // Controls
  onClose,
  onTriggerExecution,
  onSimulateExecution,
  workflowRunId,
}) {
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [demoStartTime, setDemoStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  // Start demo timer
  useEffect(() => {
    if (!demoStartTime) {
      setDemoStartTime(Date.now());
    }
    timerRef.current = setInterval(() => {
      setElapsed(Date.now() - (demoStartTime || Date.now()));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [demoStartTime]);

  // Auto-complete Step 1 when enforcement report exists
  useEffect(() => {
    if (enforcementReport && enforcementReport.stats?.enforcementNodesAdded > 0) {
      setCompletedSteps(prev => new Set([...prev, 1]));
    }
  }, [enforcementReport]);

  // Auto-advance to Step 2 after Step 1 completes
  useEffect(() => {
    if (completedSteps.has(1) && activeStep === 1) {
      setTimeout(() => setActiveStep(2), 1500);
    }
  }, [completedSteps, activeStep]);

  // Auto-complete Step 2 when execution starts
  useEffect(() => {
    if (isExecuting && activeRunId) {
      setCompletedSteps(prev => new Set([...prev, 2]));
    }
  }, [isExecuting, activeRunId]);

  // Auto-advance to Step 3 when enforcement events arrive
  useEffect(() => {
    if (enforcementEvents.length > 0 && activeStep === 2) {
      setActiveStep(3);
    }
  }, [enforcementEvents, activeStep]);

  // Auto-advance past Step 2 when execution finishes (success or failure)
  // This prevents the demo from getting stuck on Step 2 if execution fails
  useEffect(() => {
    if (!isExecuting && completedSteps.has(2) && activeStep === 2) {
      setTimeout(() => setActiveStep(3), 500);
    }
  }, [isExecuting, completedSteps, activeStep]);

  // Mark step complete and advance
  const advanceStep = useCallback((stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
    if (stepId < 7) {
      setActiveStep(stepId + 1);
    }
  }, []);

  const formatElapsed = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  // Render the active step's content panel
  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <div style={{ padding: 16 }}>
            <div style={{
              background: enforcementReport ? '#e3f4f1' : '#fafaf9',
              border: `1px solid ${enforcementReport ? '#e7e5e4' : '#e7e5e4'}`,
              borderRadius: 8,
              padding: 16,
            }}>
              {enforcementReport ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>✅</span>
                    <span style={{ fontWeight: 700, color: '#0a766b', fontSize: 15 }}>
                      {enforcementReport.stats.enforcementNodesAdded} Governance Nodes Inserted
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {enforcementReport.insertionReport.map((entry, i) => (
                      <div key={i} style={{
                        padding: '4px 10px',
                        background: '#fff',
                        border: '1px solid #e7e5e4',
                        borderRadius: 6,
                        fontSize: 11,
                        color: '#44403c',
                      }}>
                        <strong>{entry.enforcementType}</strong>
                        <span style={{ color: '#a8a29e', margin: '0 4px' }}>→</span>
                        {entry.reason}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => advanceStep(1)}
                    style={{
                      marginTop: 16,
                      width: '100%',
                      padding: '10px 16px',
                      background: 'linear-gradient(135deg, #0d9488, #0a766b)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Continue → Live Execution
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#78716c' }}>
                  <span style={{ fontSize: 28 }}>📋</span>
                  <p style={{ fontSize: 13, marginTop: 8 }}>
                    Load the <strong>"Phishing Email Triage"</strong> playbook from the template library to see protection nodes auto-insert.
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ padding: 16 }}>
            <div style={{
              background: isExecuting ? '#e3f4f1' : '#fafaf9',
              border: `1px solid ${isExecuting ? '#e7e5e4' : '#e7e5e4'}`,
              borderRadius: 8,
              padding: 16,
              textAlign: 'center',
            }}>
              {isExecuting ? (
                <>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>⚡</div>
                  <div style={{ fontWeight: 700, color: '#0a766b', fontSize: 15 }}>Workflow Executing...</div>
                  <div style={{ fontSize: 12, color: '#78716c', marginTop: 4 }}>
                    Watch the canvas — nodes light up as each step executes in real-time.
                  </div>
                  <div style={{
                    marginTop: 12,
                    padding: '6px 12px',
                    background: '#e3f4f1',
                    borderRadius: 6,
                    fontSize: 11,
                    color: '#0a766b',
                    fontFamily: 'monospace',
                  }}>
                    Run ID: {activeRunId?.substring(0, 12)}...
                  </div>
                  <button
                    onClick={() => advanceStep(2)}
                    style={{
                      marginTop: 16,
                      width: '100%',
                      padding: '10px 16px',
                      background: '#0d9488',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Continue → Data Protection Guard
                  </button>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 28 }}>▶️</span>
                  <p style={{ fontSize: 13, marginTop: 8, color: '#78716c' }}>
                    Click <strong>"Execute"</strong> in the toolbar to trigger the workflow.
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {onTriggerExecution && (
                      <button
                        onClick={onTriggerExecution}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          background: '#0d9488',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        ⚡ Execute Workflow
                      </button>
                    )}
                    {onSimulateExecution && (
                      <button
                        onClick={onSimulateExecution}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #0d9488, #0a766b)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        🎬 Simulate Demo
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div style={{ padding: 8 }}>
            <DpgScanPanel enforcementEvents={enforcementEvents} />
            <button
              onClick={() => advanceStep(3)}
              style={{
                marginTop: 12,
                width: '100%',
                padding: '10px 16px',
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Continue → Token Vault
            </button>
          </div>
        );

      case 4:
        return (
          <div style={{ padding: 8 }}>
            <TokenVaultExplorer tokens={tokens} workflowRunId={workflowRunId} />
            <button
              onClick={() => advanceStep(4)}
              style={{
                marginTop: 12,
                width: '100%',
                padding: '10px 16px',
                background: '#0a766b',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Continue → Compliance Certificate
            </button>
          </div>
        );

      case 5:
        return (
          <div style={{ padding: 8 }}>
            <ComplianceCertificateViewer
              certificate={certificate}
              onGenerate={onGenerateCertificate}
            />
            <button
              onClick={() => advanceStep(5)}
              style={{
                marginTop: 12,
                width: '100%',
                padding: '10px 16px',
                background: '#0a766b',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Continue → Agent Trust Score
            </button>
          </div>
        );

      case 6:
        return (
          <div style={{ padding: 8 }}>
            <AgentTrustScorePanel
              complianceEvents={complianceEvents}
              agentData={agentData}
            />
            <button
              onClick={() => advanceStep(6)}
              style={{
                marginTop: 12,
                width: '100%',
                padding: '10px 16px',
                background: '#0d9488',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Continue → Protection Proof
            </button>
          </div>
        );

      case 7:
        return (
          <div style={{ padding: 8 }}>
            <GovernanceProofSummary
              enforcementReport={enforcementReport}
              enforcementEvents={enforcementEvents}
              complianceEvents={complianceEvents}
              certificate={certificate}
              workflowName={workflowName}
              executionDurationMs={executionDurationMs || elapsed}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      width: 420,
      background: '#fff',
      borderLeft: '1px solid #e7e5e4',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%',
    }}>
      {/* Demo Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #44403c 100%)',
        color: '#fff',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 18 }}>🎯</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: -0.3 }}>
            Demo Sequence
          </div>
          <div style={{ fontSize: 11, color: '#a8a29e', marginTop: 1 }}>
            15 min · Protected AI Agent Orchestration
          </div>
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          padding: '4px 10px',
          borderRadius: 6,
          fontSize: 12,
          fontFamily: 'monospace',
          fontWeight: 600,
          color: '#f59e0b',
        }}>
          {formatElapsed(elapsed)}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            color: '#a8a29e',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 16,
            lineHeight: 1,
          }}
          title="Exit Demo Mode"
        >
          ✕
        </button>
      </div>

      {/* Step Navigation */}
      <div style={{
        padding: '8px 12px',
        background: '#fafaf9',
        borderBottom: '1px solid #e7e5e4',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flexShrink: 0,
        maxHeight: 220,
        overflowY: 'auto',
      }}>
        {DEMO_STEPS.map((step) => {
          const isActive = activeStep === step.id;
          const isComplete = completedSteps.has(step.id);
          return (
            <div
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 6,
                cursor: 'pointer',
                background: isActive
                  ? `linear-gradient(135deg, ${step.color}15, ${step.color}08)`
                  : 'transparent',
                border: isActive
                  ? `1px solid ${step.color}30`
                  : '1px solid transparent',
                transition: 'all 0.2s ease',
              }}
            >
              {/* Step indicator */}
              <div style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isComplete ? 12 : 11,
                fontWeight: 700,
                flexShrink: 0,
                background: isComplete
                  ? '#0a766b'
                  : isActive
                    ? step.color
                    : '#e7e5e4',
                color: (isComplete || isActive) ? '#fff' : '#a8a29e',
                transition: 'all 0.3s ease',
              }}>
                {isComplete ? '✓' : step.id}
              </div>

              {/* Step info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#1c1917' : isComplete ? '#0a766b' : '#78716c',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {step.icon} {step.title}
                </div>
              </div>

              {/* Duration badge */}
              <span style={{
                fontSize: 9,
                color: '#a8a29e',
                fontWeight: 500,
                flexShrink: 0,
              }}>
                {step.duration}
              </span>
            </div>
          );
        })}
      </div>

      {/* Active Step Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e7e5e4',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{
            fontSize: 16,
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `${DEMO_STEPS[activeStep - 1].color}15`,
          }}>
            {DEMO_STEPS[activeStep - 1].icon}
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1c1917' }}>
              Step {activeStep}: {DEMO_STEPS[activeStep - 1].title}
            </div>
            <div style={{ fontSize: 11, color: '#78716c', marginTop: 1 }}>
              {DEMO_STEPS[activeStep - 1].subtitle}
            </div>
          </div>
        </div>
        <div style={{
          fontSize: 11,
          color: '#57534e',
          background: '#f5f5f4',
          padding: '6px 10px',
          borderRadius: 4,
          lineHeight: 1.4,
          marginTop: 8,
        }}>
          💡 {DEMO_STEPS[activeStep - 1].instruction}
        </div>
      </div>

      {/* Step Content — scrollable */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}>
        {renderStepContent()}
      </div>

      {/* Demo Progress Footer */}
      <div style={{
        padding: '8px 16px',
        background: '#fafaf9',
        borderTop: '1px solid #e7e5e4',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }}>
        <div style={{
          flex: 1,
          height: 4,
          background: '#e7e5e4',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${(completedSteps.size / 7) * 100}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #0d9488, #0a766b, #059669)',
            borderRadius: 2,
            transition: 'width 0.5s ease',
          }} />
        </div>
        <span style={{ fontSize: 11, color: '#78716c', fontWeight: 600, flexShrink: 0 }}>
          {completedSteps.size}/7
        </span>
      </div>
    </div>
  );
}

export default DemoSequencePanel;
